import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLocationContext } from "@/contexts/LocationContext";
import { useSocket } from "@/lib/socket";
import { useToast } from "./ui/use-toast";
import { useState } from "react";

interface MoveLocationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  orderNumber: string;
  currentLocation: string;
}

export function MoveLocationDialog({ isOpen, onClose, orderNumber, currentLocation }: MoveLocationDialogProps) {
  const { locations } = useLocationContext();
  const socket = useSocket();
  const { toast } = useToast();
  const [loadingLocation, setLoadingLocation] = useState<string | null>(null);

  const handleMove = async (newLocation: string) => {
    if (socket) {
      setLoadingLocation(newLocation);
      socket.emit('moveOrder', {
        salesOrderNumber: orderNumber,
        location: newLocation
      });

      // Listen for success
      socket.once('orderMoveSuccess', () => {
        setLoadingLocation(null);
        toast({
          title: 'Order Moved',
          description: `Order ${orderNumber} moved to ${newLocation}`,
        });
        onClose();
      });

      // Listen for errors
      socket.once('netsuiteError', (error) => {
        setLoadingLocation(null);
        toast({
          title: 'Error',
          description: error.message,
          variant: 'destructive'
        });
      });
    }
  };

  const availableLocations = locations
    .filter(loc => loc.state !== 'USA')
    .map(loc => `${loc.city} ${loc.state}`)
    .filter(loc => loc !== currentLocation);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Move Order #{orderNumber}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-2">
          {availableLocations.map((location) => (
            <Button
              key={location}
              variant="outline"
              className="w-full justify-start"
              onClick={() => handleMove(location)}
              disabled={loadingLocation === location}
            >
              {loadingLocation === location ? (
                <span className="flex items-center gap-2">
                  <span className="animate-pulse">Moving to {location}...</span>
                </span>
              ) : (
                location
              )}
            </Button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
} 